import React, { useState, useRef, useMemo } from 'react';
import {
  Download,
  Upload,
  CheckCircle,
  AlertTriangle,
  FileText,
  ArrowLeft,
  ArrowRight,
  Info,
  FileSpreadsheet,
  RotateCcw,
  X,
  Shield,
  Loader,
} from 'lucide-react';
import {
  generateStudentTemplate,
  TEMPLATE_COLUMNS,
  REQUIRED_COLUMNS,
  AUTO_COLUMNS,
  STUDENTCODE_COLUMNS,
} from './utils/studentTemplateGenerator';
import {
  parseStudentExcel,
  StudentRow,
  ValidationError,
  getRowErrors,
  rowHasErrors,
  ParseResult,
} from './utils/studentValidator';
import { validateStudentData, enrollStudents, EnrollmentRowResult } from '../../_services/ingestion.service';
import './data-ingestion.css';

type Step = 1 | 2 | 3 | 4 | 5;
type FilterMode = 'all' | 'valid' | 'errors';

const STEP_LABELS = ['Instructions', 'Upload', 'Validate & Preview', 'Submit', 'Result'];

const DataIngestionPage: React.FC = () => {
  const [currentStep, setCurrentStep] = useState<Step>(1);

  // Upload
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [fileName, setFileName] = useState<string | null>(null);
  const [fileError, setFileError] = useState<string | null>(null);
  const [dragActive, setDragActive] = useState(false);

  // Parse / validation
  const [rows, setRows] = useState<StudentRow[]>([]);
  const [validationErrors, setValidationErrors] = useState<ValidationError[]>([]);
  const [missingColumns, setMissingColumns] = useState<string[]>([]);
  const [filter, setFilter] = useState<FilterMode>('all');

  // Server validation
  const [validating, setValidating] = useState(false);
  const [serverValidated, setServerValidated] = useState(false);

  // Submit
  const [submitting, setSubmitting] = useState(false);
  const [submitProgress, setSubmitProgress] = useState(0);
  const [submitResult, setSubmitResult] = useState<{
    success: boolean;
    enrolled: number;
    failed: number;
    total: number;
    results: EnrollmentRowResult[];
  } | null>(null);

  // Derived
  const validCount = useMemo(
    () => rows.filter((_, i) => !rowHasErrors(validationErrors, i)).length,
    [rows, validationErrors]
  );
  const errorCount = rows.length - validCount;

  const filteredRows = useMemo(() => {
    if (filter === 'valid') return rows.map((r, i) => ({ row: r, idx: i })).filter(x => !rowHasErrors(validationErrors, x.idx));
    if (filter === 'errors') return rows.map((r, i) => ({ row: r, idx: i })).filter(x => rowHasErrors(validationErrors, x.idx));
    return rows.map((r, i) => ({ row: r, idx: i }));
  }, [rows, validationErrors, filter]);

  // --- Handlers ---

  const handleDownloadTemplate = () => {
    generateStudentTemplate();
  };

  const processFile = async (file: File) => {
    const validExtensions = ['.xlsx', '.xls'];
    const ext = file.name.substring(file.name.lastIndexOf('.')).toLowerCase();

    if (!validExtensions.includes(ext)) {
      setFileError('Please upload an Excel file (.xlsx or .xls)');
      return;
    }

    try {
      setFileError(null);
      setFileName(file.name);
      const buffer = await file.arrayBuffer();
      const result: ParseResult = parseStudentExcel(buffer);

      if (result.rows.length === 0) {
        setFileError('No data rows found. Make sure the first sheet has column headers and data rows.');
        return;
      }

      if (result.missingColumns.length > 0) {
        setFileError(`Missing required columns: ${result.missingColumns.join(', ')}`);
        return;
      }

      setRows(result.rows);
      setValidationErrors(result.errors);
      setMissingColumns(result.missingColumns);
      setCurrentStep(3);
    } catch (err: any) {
      setFileError(`Failed to parse file: ${err.message}`);
    }
  };

  const handleFileSelect = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    await processFile(file);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(true);
  };

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
  };

  const handleDrop = async (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    const file = e.dataTransfer.files?.[0];
    if (file) await processFile(file);
  };

  const handleServerValidation = async () => {
    if (validating) return;
    setValidating(true);
    try {
      const response = await validateStudentData(rows);
      if (response.success && response.data) {
        const serverErrors = response.data.errors;
        if (serverErrors.length > 0) {
          // Merge server errors with existing client errors (avoid duplicates)
          const existingKeys = new Set(
            validationErrors.map(e => `${e.row}_${e.field}_${e.message}`)
          );
          const newErrors = serverErrors.filter(
            e => !existingKeys.has(`${e.row}_${e.field}_${e.message}`)
          );
          setValidationErrors(prev => [...prev, ...newErrors]);
          setServerValidated(false);
        } else {
          setServerValidated(true);
        }
      }
    } catch (err: any) {
      setFileError(`Server validation failed: ${err.message}`);
    } finally {
      setValidating(false);
    }
  };

  const handleSubmit = async () => {
    if (submitting) return;

    // If not yet server-validated, run server validation first
    if (!serverValidated) {
      await handleServerValidation();
      return;
    }

    setSubmitting(true);
    setCurrentStep(4);
    setSubmitProgress(0);

    try {
      // Show indeterminate progress while API call is in flight
      setSubmitProgress(30);
      const response = await enrollStudents(rows);
      setSubmitProgress(100);

      if (response.success && response.summary && response.results) {
        setSubmitResult({
          success: true,
          enrolled: response.summary.enrolled,
          failed: response.summary.failed,
          total: response.summary.total,
          results: response.results,
        });
      } else {
        setSubmitResult({
          success: false,
          enrolled: 0,
          failed: rows.length,
          total: rows.length,
          results: [],
        });
      }
      setCurrentStep(5);
    } catch (err: any) {
      setSubmitResult({
        success: false,
        enrolled: 0,
        failed: rows.length,
        total: rows.length,
        results: [],
      });
      setFileError(`Enrollment failed: ${err.message}`);
      setCurrentStep(5);
    } finally {
      setSubmitting(false);
    }
  };

  const handleReset = () => {
    setCurrentStep(1);
    setFileName(null);
    setFileError(null);
    setRows([]);
    setValidationErrors([]);
    setMissingColumns([]);
    setFilter('all');
    setValidating(false);
    setServerValidated(false);
    setSubmitting(false);
    setSubmitProgress(0);
    setSubmitResult(null);
  };

  // --- Step Renderers ---

  const renderInstructions = () => (
    <div className="di-instructions">
      <div className="di-info-banner">
        <Info size={20} />
        <span>Use this tool to bulk import student enrollment records. Download the template, fill in the data, and upload it back.</span>
      </div>

      <div className="di-columns-section">
        <h3>Template Columns</h3>
        <div className="di-columns-grid">
          {TEMPLATE_COLUMNS.map(col => (
            <div key={col} className="di-column-item">
              <span className="di-column-name">{col}</span>
              {REQUIRED_COLUMNS.includes(col as any) && <span className="di-required-badge">Required</span>}
              {AUTO_COLUMNS.includes(col as any) && <span className="di-auto-badge">AUTO</span>}
              {STUDENTCODE_COLUMNS.includes(col as any) && (
                <span
                  className="di-auto-badge"
                  style={{ background: 'var(--status-success-bg)', color: 'var(--status-success-text)' }}
                  title="Use STUDENTCODE to set this cell to the resolved StudentCode"
                >
                  STUDENTCODE
                </span>
              )}
            </div>
          ))}
        </div>
      </div>

      <div className="di-auto-info">
        <h3>Keywords you can use</h3>
        <p>
          Instead of typing a value, the highlighted columns accept these
          case-insensitive keywords:
        </p>
        <ul style={{ marginTop: 8, marginBottom: 0 }}>
          <li>
            <code>AUTO</code> on <strong>StudentCode</strong> — generated from
            the batch's code series.
          </li>
          <li>
            <code>AUTO</code> on <strong>UserName</strong> — mirrors the
            resolved <strong>StudentCode</strong>.
          </li>
          <li>
            <code>AUTO</code> on <strong>Password</strong> — system generates a
            random 8-character password.
          </li>
          <li>
            <code>STUDENTCODE</code> on <strong>UserName</strong> or{" "}
            <strong>Password</strong> — explicitly mirrors the resolved
            <strong> StudentCode</strong> (handy when you want a single
            memorable credential per student).
          </li>
        </ul>
      </div>

      <div className="di-sample-section">
        <h3>Sample Data</h3>
        <div className="di-sample-table-wrapper">
          <table className="di-sample-table">
            <thead>
              <tr>
                {TEMPLATE_COLUMNS.map(col => <th key={col}>{col}</th>)}
              </tr>
            </thead>
            <tbody>
              <tr>
                <td>1</td>
                <td>STU001</td>
                <td>BATCH-A</td>
                <td>STU001</td>
                <td>STU001</td>
              </tr>
              <tr>
                <td>2</td>
                <td className="di-auto-value">AUTO</td>
                <td>BATCH-B</td>
                <td className="di-auto-value">AUTO</td>
                <td className="di-auto-value">AUTO</td>
              </tr>
              <tr>
                <td>3</td>
                <td className="di-auto-value">AUTO</td>
                <td>BATCH-B</td>
                <td className="di-auto-value">STUDENTCODE</td>
                <td className="di-auto-value">STUDENTCODE</td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>

      <div className="di-tips">
        <h3>Tips</h3>
        <ul>
          <li>Fill data in the <strong>Students</strong> sheet only. The Instructions sheet is for reference.</li>
          <li>Do not change or remove the header row.</li>
          <li><strong>EnquiryId</strong>, <strong>StudentCode</strong>, and <strong>BatchCode</strong> are mandatory for every row.</li>
          <li>
            Use <strong>AUTO</strong> on StudentCode, UserName, or Password to
            let the system generate the value (StudentCode → series, UserName
            → StudentCode, Password → random 8 chars).
          </li>
          <li>
            Use <strong>STUDENTCODE</strong> on UserName or Password to copy
            the resolved StudentCode into that cell.
          </li>
          <li>StudentCode must be unique (unless AUTO).</li>
        </ul>
      </div>

      <div className="di-actions">
        <button className="di-btn-primary" onClick={handleDownloadTemplate}>
          <Download size={18} />
          Download Template
        </button>
        <button className="di-btn-primary" onClick={() => setCurrentStep(2)}>
          Next: Upload File
          <ArrowRight size={18} />
        </button>
      </div>
    </div>
  );

  const renderUpload = () => (
    <div className="di-upload">
      <div
        className={`di-dropzone ${dragActive ? 'drag-active' : ''} ${fileName ? 'has-file' : ''}`}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
        onClick={() => fileInputRef.current?.click()}
      >
        <input
          ref={fileInputRef}
          type="file"
          accept=".xlsx,.xls"
          onChange={handleFileSelect}
          style={{ display: 'none' }}
        />
        <div className="di-dropzone-icon">
          {fileName ? <FileSpreadsheet size={48} /> : <Upload size={48} />}
        </div>
        <div className="di-dropzone-text">
          {fileName ? (
            <>
              <strong>{fileName}</strong>
              <p>Click or drop to replace</p>
            </>
          ) : (
            <>
              <strong>Drag & drop your Excel file here</strong>
              <p>or click to browse. Accepted formats: .xlsx, .xls</p>
            </>
          )}
        </div>
      </div>

      {fileError && (
        <div className="di-error-banner">
          <AlertTriangle size={18} />
          <span>{fileError}</span>
          <button className="di-error-close" onClick={() => setFileError(null)}>
            <X size={16} />
          </button>
        </div>
      )}

      <div className="di-actions">
        <button className="di-btn-secondary" onClick={() => setCurrentStep(1)}>
          <ArrowLeft size={18} />
          Back
        </button>
        <button className="di-btn-secondary" onClick={handleDownloadTemplate}>
          <Download size={18} />
          Download Template
        </button>
      </div>
    </div>
  );

  const renderPreview = () => (
    <div className="di-preview">
      <div className="di-summary-bar">
        <div className="di-summary-item">
          <FileText size={18} />
          <span><strong>{rows.length}</strong> Total Rows</span>
        </div>
        <div className="di-summary-item di-summary-valid">
          <CheckCircle size={18} />
          <span><strong>{validCount}</strong> Valid</span>
        </div>
        <div className="di-summary-item di-summary-error">
          <AlertTriangle size={18} />
          <span><strong>{errorCount}</strong> Errors</span>
        </div>
      </div>

      {missingColumns.length > 0 && (
        <div className="di-error-banner">
          <AlertTriangle size={18} />
          <span>Missing required columns: {missingColumns.join(', ')}</span>
        </div>
      )}

      <div className="di-filter-tabs">
        <button className={`di-filter-tab ${filter === 'all' ? 'active' : ''}`} onClick={() => setFilter('all')}>
          All ({rows.length})
        </button>
        <button className={`di-filter-tab ${filter === 'valid' ? 'active' : ''}`} onClick={() => setFilter('valid')}>
          Valid ({validCount})
        </button>
        <button className={`di-filter-tab ${filter === 'errors' ? 'active' : ''}`} onClick={() => setFilter('errors')}>
          Errors ({errorCount})
        </button>
      </div>

      <div className="di-table-wrapper">
        <table className="di-preview-table">
          <thead>
            <tr>
              <th className="di-th-row">#</th>
              <th className="di-th-status">Status</th>
              {TEMPLATE_COLUMNS.map(col => (
                <th key={col}>{col}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {filteredRows.map(({ row, idx }) => {
              const hasError = rowHasErrors(validationErrors, idx);
              const rowErrors = getRowErrors(validationErrors, idx);
              const errorFields = new Set(rowErrors.map(e => e.field));
              return (
                <React.Fragment key={idx}>
                  <tr className={hasError ? 'di-row-error' : 'di-row-valid'}>
                    <td className="di-td-row">{idx + 2}</td>
                    <td className="di-td-status">
                      {hasError ? (
                        <span className="di-status-badge di-status-error">
                          <AlertTriangle size={14} /> {rowErrors.length} error{rowErrors.length > 1 ? 's' : ''}
                        </span>
                      ) : (
                        <span className="di-status-badge di-status-valid">
                          <CheckCircle size={14} /> Valid
                        </span>
                      )}
                    </td>
                    {TEMPLATE_COLUMNS.map(col => (
                      <td key={col} className={errorFields.has(col) ? 'di-cell-error' : ''}>
                        {row[col] || <span className="di-empty-cell">-</span>}
                      </td>
                    ))}
                  </tr>
                  {hasError && (
                    <tr className="di-error-detail-row">
                      <td colSpan={TEMPLATE_COLUMNS.length + 2}>
                        <div className="di-error-details">
                          {rowErrors.map((err, ei) => (
                            <span key={ei} className="di-error-tag">
                              <strong>{err.field}:</strong> {err.message}
                            </span>
                          ))}
                        </div>
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              );
            })}
          </tbody>
        </table>
      </div>

      {fileError && (
        <div className="di-error-banner" style={{ marginTop: 16 }}>
          <AlertTriangle size={18} />
          <span>{fileError}</span>
          <button className="di-error-close" onClick={() => setFileError(null)}>
            <X size={16} />
          </button>
        </div>
      )}

      <div className="di-actions">
        <button
          className="di-btn-secondary"
          onClick={() => {
            setCurrentStep(2);
            setRows([]);
            setValidationErrors([]);
            setFilter('all');
            setServerValidated(false);
          }}
        >
          <ArrowLeft size={18} />
          Back to Upload
        </button>
        {!serverValidated ? (
          <button
            className="di-btn-primary di-btn-validate"
            onClick={handleSubmit}
            disabled={validCount === 0 || validating || submitting}
          >
            {validating ? (
              <>
                <Loader size={18} className="di-spin" />
                Validating...
              </>
            ) : (
              <>
                <Shield size={18} />
                Validate with Server
              </>
            )}
          </button>
        ) : (
          <button className="di-btn-primary" onClick={handleSubmit} disabled={validCount === 0 || submitting}>
            {submitting ? (
              <>
                <Loader size={18} className="di-spin" />
                Submitting...
              </>
            ) : (
              <>
                Submit {validCount} Row{validCount !== 1 ? 's' : ''}
                <ArrowRight size={18} />
              </>
            )}
          </button>
        )}
      </div>
    </div>
  );

  const renderSubmitting = () => (
    <div className="di-submitting">
      <div className="di-progress-container">
        <div className="di-progress-icon">
          <Upload size={40} className="di-spin" />
        </div>
        <h3>Uploading {validCount} records...</h3>
        <div className="di-progress-bar">
          <div className="di-progress-fill" style={{ width: `${submitProgress}%` }} />
        </div>
        <p className="di-progress-text">{submitProgress}% complete</p>
      </div>
    </div>
  );

  const handleDownloadResults = () => {
    if (!submitResult?.results) return;
    const successResults = submitResult.results.filter(r => r.success);
    if (successResults.length === 0) return;

    // Build CSV content
    const headers = ['Row', 'Batch', 'StudentCode', 'UserName', 'Password', 'ParentUserName', 'EnrollmentId'];
    const csvRows = successResults.map(r =>
      [r.row + 2, rows[r.row]?.BatchCode || '', r.studentCode, r.userName, r.password, r.parentUserName, r.enrollmentId].join(',')
    );
    const csv = [headers.join(','), ...csvRows].join('\n');

    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = 'enrollment-results.csv';
    link.click();
    URL.revokeObjectURL(url);
  };

  const renderResult = () => (
    <div className="di-result">
      {submitResult && submitResult.enrolled > 0 ? (
        <div className="di-result-success">
          <div className="di-result-icon di-result-icon-success">
            <CheckCircle size={48} />
          </div>
          <h2>Import {submitResult.failed === 0 ? 'Successful' : 'Partially Successful'}</h2>
          <p>
            {submitResult.enrolled} of {submitResult.total} student record{submitResult.total !== 1 ? 's' : ''} enrolled successfully.
            {submitResult.failed > 0 && ` ${submitResult.failed} failed.`}
          </p>

          <div className="di-result-stats">
            <div className="di-stat-card">
              <div className="di-stat-value">{submitResult.enrolled}</div>
              <div className="di-stat-label">Enrolled</div>
            </div>
            <div className="di-stat-card">
              <div className="di-stat-value">{submitResult.failed}</div>
              <div className="di-stat-label">Failed</div>
            </div>
          </div>
        </div>
      ) : (
        <div className="di-result-error">
          <div className="di-result-icon di-result-icon-error">
            <AlertTriangle size={48} />
          </div>
          <h2>Import Failed</h2>
          <p>{fileError || 'Something went wrong during the import. Please try again.'}</p>
        </div>
      )}

      {submitResult?.results && submitResult.results.length > 0 && (
        <div className="di-results-table-section">
          <h3>Enrollment Results</h3>
          <div className="di-table-wrapper">
            <table className="di-preview-table">
              <thead>
                <tr>
                  <th>#</th>
                  <th>Status</th>
                  <th>Batch</th>
                  <th>Student Code</th>
                  <th>User Name</th>
                  <th>Password</th>
                  <th>Parent User</th>
                  <th>Details</th>
                </tr>
              </thead>
              <tbody>
                {submitResult.results.map((r) => (
                  <tr key={r.row} className={r.success ? 'di-row-valid' : 'di-row-error'}>
                    <td>{r.row + 2}</td>
                    <td>
                      {r.success ? (
                        <span className="di-status-badge di-status-valid">
                          <CheckCircle size={14} /> Enrolled
                        </span>
                      ) : (
                        <span className="di-status-badge di-status-error">
                          <AlertTriangle size={14} /> Failed
                        </span>
                      )}
                    </td>
                    <td>{rows[r.row]?.BatchCode || '-'}</td>
                    <td>{r.studentCode || '-'}</td>
                    <td>{r.userName || '-'}</td>
                    <td>{r.password || '-'}</td>
                    <td>{r.parentUserName || '-'}</td>
                    <td>{r.error || `ID: ${r.enrollmentId}`}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      <div className="di-actions di-actions-center">
        {submitResult?.results && submitResult.results.some(r => r.success) && (
          <button className="di-btn-secondary" onClick={handleDownloadResults}>
            <Download size={18} />
            Download Results
          </button>
        )}
        <button className="di-btn-primary" onClick={handleReset}>
          <RotateCcw size={18} />
          Import Another File
        </button>
      </div>
    </div>
  );

  // --- Main Render ---

  return (
    <div className="data-ingestion">
      <div className="di-header">
        <h1>Student Data Ingestion</h1>
        <p>Bulk import student enrollment records from Excel</p>
      </div>

      <div className="di-stepper">
        {STEP_LABELS.map((label, idx) => {
          const stepNum = (idx + 1) as Step;
          const isActive = stepNum === currentStep;
          const isCompleted = stepNum < currentStep;
          return (
            <React.Fragment key={stepNum}>
              <div className={`di-step ${isActive ? 'active' : ''} ${isCompleted ? 'completed' : ''}`}>
                <div className="di-step-circle">
                  {isCompleted ? <CheckCircle size={16} /> : stepNum}
                </div>
                <span className="di-step-label">{label}</span>
              </div>
              {idx < STEP_LABELS.length - 1 && (
                <div className={`di-step-connector ${isCompleted ? 'completed' : ''}`} />
              )}
            </React.Fragment>
          );
        })}
      </div>

      <div className="di-content-card">
        {currentStep === 1 && renderInstructions()}
        {currentStep === 2 && renderUpload()}
        {currentStep === 3 && renderPreview()}
        {currentStep === 4 && renderSubmitting()}
        {currentStep === 5 && renderResult()}
      </div>
    </div>
  );
};

export default DataIngestionPage;
